import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../App";
import Header from "../components/Header";
import { 
  BookOpen, 
  Trophy, 
  MessageCircle, 
  Volume2, 
  Flame,
  Target,
  ChevronRight,
  BookMarked,
  Brain,
  Book,
  Star,
  Zap,
  Headphones,
  Play,
  Settings,
  PenSquare,
  Gamepad2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// African imagery
const IMAGES = {
  mask: "https://images.unsplash.com/photo-1590845947670-c009801ffa74?w=200&q=80",
  pattern: "https://images.unsplash.com/photo-1590735213920-68192a487bc2?w=400&q=80",
};

// ← AJOUT : Export Navigation pour compatibilité avec les autres pages
export const Navigation = Header;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wordOfDay, setWordOfDay] = useState(null);
  const [flashcardSets, setFlashcardSets] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [wordRes, setsRes, leaderRes] = await Promise.all([
          fetch(`${API}/words/daily`),
          fetch(`${API}/flashcards`),
          fetch(`${API}/leaderboard?limit=5`)
        ]);

        if (wordRes.ok) setWordOfDay(await wordRes.json());
        if (setsRes.ok) setFlashcardSets((await setsRes.json()).slice(0, 4));
        if (leaderRes.ok) setLeaderboard(await leaderRes.json());

        // Update streak
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${API}/users/streak`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
  }, []);

  const playAudio = async (wordId) => {
    if (isPlaying) return;
    setIsPlaying(true);
    try {
      const audio = new Audio(`${API}/words/${wordId}/audio`);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      await audio.play();
    } catch (error) {
      setIsPlaying(false);
    }
  };

  const quickActions = [
    { icon: BookMarked, label: "Flashcards", path: "/learn", color: "gradient-wax", desc: "Apprendre" },
    { icon: Brain, label: "Quiz", path: "/quiz", color: "bg-secondary", desc: "Tester" },
    { icon: Gamepad2, label: "Memory Game", path: "/memory", color: "bg-accent", desc: "Jouer" },
    { icon: Book, label: "Dictionnaire", path: "/dictionary", color: "bg-primary", desc: "Explorer" },
    // CMS Admin only for admins
    ...(user?.role === "admin" ? [{ icon: Settings, label: "CMS Admin", path: "/admin/cms", color: "bg-muted", desc: "Gérer" }] : []),
  ];

  const progressPercent = user?.words_learned ? Math.min((user.words_learned / 50) * 100, 100) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8 animate-slide-up">
            <div className="flex items-center gap-4 mb-2">
              <h1 className="font-heading text-3xl sm:text-4xl font-bold">
                Bienvenue, <span className="text-gradient-wax">{user?.name?.split(' ')[0]}</span> !
              </h1>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10">
                <Flame className="w-4 h-4 text-orange-500 streak-fire" />
                <span className="text-sm font-bold text-primary">{user?.streak || 0} jours</span>
              </div>
            </div>
            <p className="text-muted-foreground">
              Continuez votre voyage dans la langue baoulé
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="gradient-wax text-white border-0 shadow-lg animate-slide-up stagger-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm mb-1">Points Totaux</p>
                    <p className="text-3xl font-bold">{user?.points || 0}</p>
                  </div>
                  <Trophy className="w-12 h-12 text-white/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="animate-slide-up stagger-2">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm mb-1">Mots Appris</p>
                    <p className="text-3xl font-bold text-foreground">{user?.words_learned || 0}</p>
                  </div>
                  <BookOpen className="w-12 h-12 text-primary/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="animate-slide-up stagger-3">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm mb-1">Niveau</p>
                    <p className="text-2xl font-bold text-foreground capitalize">{user?.level || "débutant"}</p>
                  </div>
                  <Star className="w-12 h-12 text-yellow-500/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="animate-slide-up stagger-4">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm mb-1">Série Actuelle</p>
                    <p className="text-3xl font-bold text-foreground">{user?.streak || 0}</p>
                  </div>
                  <Flame className="w-12 h-12 text-orange-500/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Word of the Day & Quick Actions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Word of the Day */}
              {wordOfDay && (
                <Card className="overflow-hidden shadow-lg animate-slide-up stagger-5">
                  <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-primary" />
                        Mot du Jour
                      </CardTitle>
                      <button
                        onClick={() => playAudio(wordOfDay.word_id)}
                        disabled={isPlaying}
                        className="p-2.5 rounded-full bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
                        aria-label="Écouter la prononciation"
                      >
                        {isPlaying ? (
                          <Headphones className="w-5 h-5 text-white animate-pulse" />
                        ) : (
                          <Volume2 className="w-5 h-5 text-white" />
                        )}
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">En Baoulé</p>
                        <p className="text-4xl font-heading font-bold text-gradient-wax mb-2">
                          {wordOfDay.baoulé}
                        </p>
                        <p className="text-sm text-muted-foreground font-mono">
                          /{wordOfDay.phonétique}/
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Traduction</p>
                        <p className="text-2xl font-semibold text-foreground">
                          {wordOfDay.français}
                        </p>
                      </div>
                      {wordOfDay.exemple_baoulé && (
                        <div className="pt-4 border-t">
                          <p className="text-sm text-muted-foreground mb-2">Exemple</p>
                          <p className="text-base mb-1 italic text-foreground/80">
                            {wordOfDay.exemple_baoulé}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {wordOfDay.exemple_français}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <Card className="animate-slide-up stagger-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    Actions Rapides
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => navigate(action.path)}
                        className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-muted transition-all hover:scale-105 group"
                      >
                        <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow`}>
                          <action.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-foreground">{action.label}</p>
                          <p className="text-xs text-muted-foreground">{action.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Progress Section */}
              <Card className="animate-slide-up stagger-7">
                <CardHeader>
                  <CardTitle>Votre Progression</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Mots maîtrisés</span>
                      <span className="text-sm font-semibold text-foreground">{user?.words_learned || 0}/50</span>
                    </div>
                    <Progress value={progressPercent} className="h-2" />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary">{user?.quizzes_completed || 0}</p>
                      <p className="text-xs text-muted-foreground">Quiz complétés</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-secondary">{user?.contributions || 0}</p>
                      <p className="text-xs text-muted-foreground">Contributions</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent">{user?.badges?.length || 0}</p>
                      <p className="text-xs text-muted-foreground">Badges</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Leaderboard & Flashcard Sets */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <Card className="animate-slide-up stagger-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-primary" />
                    Classement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {leaderboard.map((entry, idx) => (
                      <div
                        key={entry.user_id || idx}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                          entry.user_id === user?.user_id
                            ? 'bg-primary/10 border-2 border-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            idx === 0 ? 'bg-yellow-500 text-white' :
                            idx === 1 ? 'bg-gray-400 text-white' :
                            idx === 2 ? 'bg-orange-600 text-white' :
                            'bg-muted text-muted-foreground'
                          }`}
                        >
                          {entry.rank || idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-foreground">
                            {entry.name}
                            {entry.user_id === user?.user_id && (
                              <span className="ml-2 text-xs text-primary">(Vous)</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">{entry.points}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full mt-4"
                    onClick={() => navigate('/leaderboard')}
                  >
                    Voir le classement complet
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>

              {/* Flashcard Sets */}
              <Card className="animate-slide-up stagger-9">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookMarked className="w-5 h-5 text-primary" />
                    Sets de Flashcards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {flashcardSets.map((set, idx) => (
                      <button
                        key={set.set_id || idx}
                        onClick={() => navigate(`/learn?set=${set.set_id}`)}
                        className="w-full text-left p-4 rounded-xl hover:bg-muted transition-all hover:scale-[1.02] border border-border group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm mb-1 text-foreground group-hover:text-primary transition-colors">
                              {set.titre}
                            </h4>
                            <p className="text-xs text-muted-foreground mb-2">
                              {set.description}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                                {set.cards?.length || 0} cartes
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/10 text-secondary font-medium">
                                {set.niveau}
                              </span>
                            </div>
                          </div>
                          <Play className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full mt-4"
                    onClick={() => navigate('/learn')}
                  >
                    Voir tous les sets
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
